// @ts-nocheck
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import clsx from 'clsx';

import { Stack } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import type { Editor } from '@ckeditor/ckeditor5-core';

import BasicModal from "modules/shared/components/general/BasicModal/BasicModal";
import RegenerateEmailModal from "modules/ClientCommunication/components/SuccessFailedModals/RegenerateEmailModal";
import EmailChipInput from 'modules/ClientCommunication/pages/BrandEmail/components/EmailChipInput';
import EmailSubjectInput from 'modules/ClientCommunication/pages/BrandEmail/components/EmailSubjectInput';
import { Button } from 'modules/shared/components/general/Button/Button';
import { ButtonVariants } from 'modules/shared/components/general/Button/Button.types';

import { getUsers } from 'services/users.services';
import { saveEditedEmail } from 'services/clientCommunication';

import { editorConfig } from 'modules/ClientCommunication/constants/constants';
import { QueryConstants } from 'constants/queryConstants';
import { servicesNames } from 'constants/servicesNames.constants';
import ROUTES from 'constants/routes';

import { deepEqual } from 'helpers/deepEqual';

import type { IBrandEmailOwnerResponse } from 'types/brands.types';
import type { User } from 'types/auth.types';

const handleToolbarVisibility = (editor: Editor, editable: boolean) => {
  const toolbarEl = editor.ui.view.toolbar.element;
  if (!toolbarEl) {
    return;
  }
  toolbarEl.style.display = !editable ? "none" : "";
};

interface IEmailTemplateProps extends Omit<IBrandEmailOwnerResponse, 'owners' | 'brand_name' | 'edit_required'> {}

export default function EmailTemplate({ to_emails, cc_emails, bcc_emails, subject, body, client_email_id, is_admin }: IEmailTemplateProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [editable, setEditable] = useState(false);
  const editorRef = useRef<Editor>(null);

  const [saveEmailDisabled, setSaveEmailDisabled] = useState(false);
  const [emailPayload, setEmailPayload] = useState({});

  const [ccEmailsActive, setCcEmailsActive] = useState(Boolean(cc_emails.length));
  const [bccEmailsActive, setBccEmailsActive] = useState(Boolean(bcc_emails.length));

  useEffect(() => {
    setCcEmailsActive(Boolean(cc_emails.length));
    setBccEmailsActive(Boolean(bcc_emails.length));
  }, [cc_emails, bcc_emails]);

  const [isRegenerateModalOpened, setIsRegenerateModalOpened] = useState(false);

  const { data: users } = useQuery<{ data: User[] }, Error, User[]>(
    [QueryConstants.getTeamManagementUsers],
    () => getUsers(),
    {
      placeholderData: { data: [] },
      select: ({ data }) => data.filter(user => user.is_active),
    },
  );

  const { mutate: saveMutate, isLoading: isLoadingSave } = useMutation(
    () => saveEditedEmail(client_email_id, emailPayload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([servicesNames.getBrandEmailOwners]);
        setEditable(false);
      },
    },
  );

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    handleToolbarVisibility(editor, editable);
  }, [editable]);

  const getNameByEmail = (email: string) => users.find(user => user.email === email)?.name;

  const suggestedUsers = useMemo(() => {
    const toEmails = to_emails;
    const ccEmails = emailPayload?.cc_emails ?? cc_emails;
    const bccEmails = bcc_emails;

    const excludedEmails = new Set([...toEmails, ...ccEmails, ...bccEmails]);
    return users?.filter(({ email }) => !excludedEmails.has(email)) || [];
  }, [users, to_emails, cc_emails, bcc_emails, emailPayload?.cc_emails]);

  const onChangeChipEmailInputs = useCallback((label: 'to' | 'cc' | 'bcc') => (emails: string[]) => {
    setEmailPayload(payload => ({ ...payload, [`${label}_emails`]: emails }));
  }, []);

  const onChangeSubject = (subject: string) => setEmailPayload(payload => ({ ...payload, subject }));

  const onActivateEmails = (label: 'cc' | 'bcc', activate: boolean) => {
    if (label === 'cc') {
      setCcEmailsActive(activate);
    }
    if (label === 'bcc') {
      setBccEmailsActive(activate);
    }
  };

  const onEditChange = () => {
    setEditable(editable => !editable);
  };

  const onCancel = () => {
    setEditable(false);
    setCcEmailsActive(Boolean(cc_emails.length));
    setBccEmailsActive(Boolean(bcc_emails.length));
  };

  const hasError = useCallback((error: boolean) => {
    setSaveEmailDisabled(error);
  }, []);

  const pickInitialValues = (initial, payload) =>
    Object.keys(payload).reduce((acc, key) => ({ ...acc, [key]: initial[key] }), {});

  const onSaveTemplate = () => {
    const initialEmailObjectSameToPayload = pickInitialValues({ to_emails, cc_emails, bcc_emails, subject, body }, emailPayload);
    if (deepEqual(initialEmailObjectSameToPayload, emailPayload)) {
      setEditable(false);
    } else {
      saveMutate();
    }
  };

  return (
    <div className="brand-email-container-body-email">
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        {!editable && <span className="email-subject">{subject}</span>}
        {(!editable && !is_admin) && (
          <button aria-label="edit-email" className="edit-email" onClick={onEditChange}>
            <EditOutlinedIcon fontSize="medium" style={{ color: '#0F314D' }} />
          </button>
        )}
      </Stack>
      <Stack mb={2} pb={2.5} gap={1.5} style={{ borderBottom: !editable ? '1px solid #D9D9D9' : 'none' }}>
        <EmailChipInput
          isEditable={editable}
          changeable={false}
          isCCActive={ccEmailsActive}
          isBCCActive={bccEmailsActive}
          onActivateEmails={onActivateEmails}
          suggestedUsers={suggestedUsers}
          getNameByEmail={getNameByEmail}
          label="To"
          emails={to_emails}
          onChange={onChangeChipEmailInputs('to')}
          placeholder="Add Email"
          hasError={hasError}
        />
        {ccEmailsActive && <EmailChipInput hasError={hasError} isEditable={editable} suggestedUsers={suggestedUsers} getNameByEmail={getNameByEmail} label="CC" emails={emailPayload?.cc_emails ?? cc_emails} onChange={onChangeChipEmailInputs('cc')} onActivateEmails={onActivateEmails} placeholder="Add Email" />}
        {bccEmailsActive && <EmailChipInput hasError={hasError} changeable={false} isEditable={editable} suggestedUsers={suggestedUsers} getNameByEmail={getNameByEmail} label="Bcc" emails={bcc_emails} onChange={onChangeChipEmailInputs('bcc')} onActivateEmails={onActivateEmails} placeholder="Add Email" />}
        {editable && <EmailSubjectInput subject={subject} onChange={onChangeSubject} />}
      </Stack>
      <div className={clsx('editor-wrapper', { 'editor-disabled': !editable })}>
        <CKEditor
          editor={ClassicEditor}
          disabled={!editable}
          config={editorConfig}
          className="editorasdasds"
          data={body}
          onReady={(editor) => {
            editorRef.current = editor;
            handleToolbarVisibility(editor, editable);
          }}
          onChange={(_, editor) => {
            setEmailPayload(payload => ({ ...payload, body: editor.getData() }));
          }}
        />
      </div>
      {!is_admin ? (
        editable ? (
          <Stack direction="row" justifyContent="end" gap={1} mt={2}>
            <Button
              variant={ButtonVariants.Outlined}
              style={{ height: "auto", margin: "0 16px" }}
              handleClick={onCancel}
              disabled={isLoadingSave}
            >
                  Cancel
            </Button>
            <Button
              variant={ButtonVariants.Primary}
              style={{ height: "auto", margin: 0 }}
              handleClick={onSaveTemplate}
              disabled={saveEmailDisabled || isLoadingSave}
              loading={isLoadingSave}
            >
                  Save
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="end" mt={2}>
            <Button
              variant={ButtonVariants.Outlined}
              style={{ height: "auto", margin: "0 16px" }}
              handleClick={() => setIsRegenerateModalOpened(true)}
            >
                  Regenerate
            </Button>
          </Stack>
        )
      ) : null}
      <BasicModal
        open={isRegenerateModalOpened}
        handleClose={() => setIsRegenerateModalOpened(false)}
      >
        <RegenerateEmailModal
          onClose={() => {
            setIsRegenerateModalOpened(false);
          }}
          onSuccess={() =>
            navigate(ROUTES.MONTHLY_STRATEGY_UPDATE)}
          emailId={client_email_id}
        />
      </BasicModal>
    </div>
  );
}
